import type { CaseSummary, EntryPayload, WorkspacePayload } from './types';

async function apiRequest<T>(path: string, token: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('authorization', `Bearer ${token}`);
  headers.set('accept', 'application/json');
  const response = await fetch(path, { ...init, headers });
  const payload = await response.json() as T & { error?: { message?: string } };
  if (!response.ok) throw new Error(payload.error?.message ?? '系統暫時無法處理。');
  return payload;
}

export function fetchSession(caseId: string, token: string): Promise<EntryPayload> {
  return apiRequest(`/api/session?case=${encodeURIComponent(caseId)}`, token);
}

export function fetchCases(token: string): Promise<{ cases: CaseSummary[] }> {
  return apiRequest('/api/cases', token);
}

export function createCase(token: string, input: { siteCode: string; title: string }) {
  return apiRequest<{ ok: true; case: CaseSummary }>('/api/cases', token, {
    method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(input),
  });
}

export function claimIdentity(caseId: string, userId: string, token: string) {
  return apiRequest<{ ok: true }>('/api/claim', token, {
    method: 'POST', headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ caseId, userId }),
  });
}

export function confirmOrder(caseId: string, versionId: string, token: string) {
  return apiRequest<{ ok: true; confirmedAt: string }>('/api/confirm', token, {
    method: 'POST', headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ caseId, versionId }),
  });
}

export function fetchWorkspace(caseId: string, token: string): Promise<WorkspacePayload> {
  return apiRequest(`/api/workspace?case=${encodeURIComponent(caseId)}`, token);
}

export function workspaceAction<T = { ok: true }>(token: string, body: Record<string, unknown>): Promise<T> {
  return apiRequest('/api/workspace', token, {
    method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body),
  });
}

export function submitReconReport(token: string, input: {
  caseId: string; itemId: string; clientReportId: string; content: string; observedAt: string;
}) {
  return apiRequest<{ ok: true; id: string; replayed: boolean }>('/api/recon', token, {
    method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(input),
  });
}

export function uploadReconMedia(token: string, reportId: string, file: File) {
  return apiRequest<{ ok: true; id: string }>(
    `/api/media?report=${encodeURIComponent(reportId)}&name=${encodeURIComponent(file.name)}`,
    token,
    { method: 'POST', headers: { 'content-type': file.type }, body: file },
  );
}

export async function fetchReconMedia(token: string, mediaId: string): Promise<Blob> {
  const response = await fetch(`/api/media?id=${encodeURIComponent(mediaId)}`, {
    headers: { authorization: `Bearer ${token}`, accept: 'image/*' },
  });
  if (!response.ok) {
    const payload = await response.json() as { error?: { message?: string } };
    throw new Error(payload.error?.message ?? '圖片載入失敗。');
  }
  return response.blob();
}

export function uploadOrderAttachment(token: string, caseId: string, subsectionId: string, file: File) {
  return apiRequest<{ ok: true; id: string }>(
    `/api/order-media?case=${encodeURIComponent(caseId)}&subsection=${encodeURIComponent(subsectionId)}&name=${encodeURIComponent(file.name)}`,
    token,
    { method: 'POST', headers: { 'content-type': file.type }, body: file },
  );
}

export async function fetchOrderAttachment(token: string, mediaId: string): Promise<Blob> {
  const response = await fetch(`/api/order-media?id=${encodeURIComponent(mediaId)}`, {
    headers: { authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const payload = await response.json() as { error?: { message?: string } };
    throw new Error(payload.error?.message ?? '附件載入失敗。');
  }
  return response.blob();
}
